import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Callout,
  EmptyState,
  Icon,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@obsina/design-system";
import {
  CalendarDays,
  ChevronDown,
  ChevronRight,
  FileText,
  FolderOpen,
  Inbox,
  Library,
  MessageSquare,
  Paperclip,
  Smartphone,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { AttachmentGrouping } from "@obsina/domain";
import type { AuthenticatedAccount } from "../App";
import { AppShell } from "../AppShell";
import { ControlPlaneError } from "../api";
import { enMessages } from "../messages";
import type { AppMessages } from "../messages";
import type { AppView } from "../navigation";
import { SOURCE_ITEMS_PAGE_SIZE, listSourceItems } from "./api";
import {
  TEXT_STATUSES,
  formatAsOf,
  formatByteSize,
  formatItemDate,
  groupByParent,
  kindsForChannel,
  shownTextState,
  untitledLabel,
} from "./data";
import type {
  InventoryGroup,
  SourceChannel,
  SourceCoverage,
  SourceItem,
  SourceKind,
  TextStatus,
} from "./data";
import { useSidebarPreferences } from "../sidebar-preferences";
import { sourcesMessages } from "./messages";
import type { SourcesMessages } from "./messages";
import { SourceTextDialog } from "./SourceTextDialog";
import "./sources.css";

const CHANNEL_ORDER: readonly SourceChannel[] = [
  "mail",
  "attachment",
  "document",
  "drive",
  "teams",
  "calendar",
  "whatsapp",
];

const CHANNEL_ICON: Readonly<Record<SourceChannel, LucideIcon>> = {
  mail: Inbox,
  attachment: Paperclip,
  document: FileText,
  drive: FolderOpen,
  teams: MessageSquare,
  calendar: CalendarDays,
  whatsapp: Smartphone,
};

type LoadErrorKind = "unauthorized" | "unavailable" | "generic";

function classifyError(error: unknown): LoadErrorKind {
  if (error instanceof ControlPlaneError) {
    if (error.status === 401) return "unauthorized";
    if (error.status === 503) return "unavailable";
  }
  return "generic";
}

/**
 * The one refusal a paging surface is expected to act on rather than report.
 *
 * A refused cursor cannot be retried: the retry re-sends the same token, so a
 * button that offers to try again cannot succeed. The instruction the boundary
 * gives is to restart pagination, and {@link restartForward} is this surface
 * carrying it out.
 *
 * This used to fire constantly. The cursor was refused whenever the coverage
 * snapshot moved, and that digest advances with the sync watermark — measured
 * on staging on 2026-08-07, page 1 at 03:13:11Z and "load more" at 03:19:21Z
 * answered `400 invalid_cursor`. The boundary no longer refuses a keyset walk
 * for that reason. What still refuses it is a cursor that is no longer a
 * continuation of this question at all: a different `asOf`, a different filter
 * set, an expired token. Those are rare, and a restart is the right answer to
 * each of them.
 */
function isSnapshotRestart(error: unknown): boolean {
  return (
    error instanceof ControlPlaneError &&
    error.status === 400 &&
    error.code === "invalid_cursor"
  );
}

/**
 * How many pages one restart will re-read. It bounds a loop whose exit is a
 * server-supplied cursor, so it is a ceiling on work, not a page limit: a
 * reader who has walked past it is told plainly rather than paged silently.
 */
const MAX_RESTART_PAGES = 20;

interface ForwardRead {
  readonly items: readonly SourceItem[];
  readonly coverage: SourceCoverage | null;
  readonly reason: string | null;
  readonly answered: boolean;
  readonly nextCursor: string | null;
}

/**
 * Reads the same question from the start under a fresh `asOf`, until at least
 * `minimum` rows are in hand or the inventory ends.
 *
 * Every page after the first travels on the cursor the page before it issued,
 * so the whole walk stays inside one snapshot — the requests are seconds apart,
 * where the read that failed was minutes old. A snapshot that moves mid-walk
 * throws, and the caller reports it rather than looping on it.
 */
async function restartForward(
  apiBase: string,
  asOf: string,
  query: {
    readonly sourceKinds: readonly SourceKind[] | null;
    readonly textStatuses: readonly string[] | null;
  },
  minimum: number,
): Promise<ForwardRead> {
  const items: SourceItem[] = [];
  let cursor: string | null = null;
  let last: Awaited<ReturnType<typeof listSourceItems>> | null = null;
  for (let page = 0; page < MAX_RESTART_PAGES; page += 1) {
    const result = await listSourceItems(apiBase, { ...query, asOf, cursor });
    last = result;
    items.push(...result.items);
    cursor = result.nextCursor;
    if (cursor === null || items.length >= minimum) break;
  }
  if (last === null) throw new ControlPlaneError("No page was read.");
  return {
    items,
    coverage: last.coverage,
    reason: last.reason,
    answered: last.status === "ok",
    nextCursor: cursor,
  };
}

interface InventoryState {
  readonly items: readonly SourceItem[];
  readonly coverage: SourceCoverage | null;
  readonly reason: string | null;
  readonly answered: boolean;
  readonly nextCursor: string | null;
  readonly initialLoading: boolean;
  readonly loadingMore: boolean;
  readonly loadMoreError: boolean;
  /**
   * Set when the page the reader asked for arrived only after the list was
   * re-read from the start. The rows below them changed underneath, and a
   * surface that swapped them in silently would be claiming a continuity it
   * does not have.
   */
  readonly restarted: boolean;
  readonly errorKind: LoadErrorKind | null;
}

interface Inventory extends InventoryState {
  readonly queryAsOf: string | null;
  loadMore: () => void;
  reload: () => void;
}

/**
 * One page of the inventory at a time, pinned to the `asOf` the first request
 * used. `asOf` is the coverage cut-off the cursor is bound to, so re-stamping it
 * per request would invalidate the chain mid-scroll; the surface therefore holds
 * it for the life of one filter selection and takes a fresh one only when the
 * question changes.
 */
function useInventory(
  apiBase: string | null,
  channel: SourceChannel | null,
  status: TextStatus | null,
): Inventory {
  const [state, setState] = useState<InventoryState>({
    items: [],
    coverage: null,
    reason: null,
    answered: false,
    nextCursor: null,
    initialLoading: Boolean(apiBase),
    loadingMore: false,
    loadMoreError: false,
    restarted: false,
    errorKind: null,
  });
  const [queryAsOf, setQueryAsOf] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);
  const mounted = useRef(true);
  // Bumped on every reload so a load-more resolving late cannot append one
  // question's rows onto another's.
  const generation = useRef(0);
  // The state the next `loadMore` decides from. It is read outside the state
  // updater on purpose: issuing the request from inside one made StrictMode's
  // second invocation fire a second identical request, and the guard the
  // updater checked (`loadingMore`) had not been committed yet to stop it.
  const latest = useRef(state);
  latest.current = state;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    generation.current += 1;
    const current = generation.current;
    if (!apiBase) {
      setState((previous) => ({
        ...previous,
        initialLoading: false,
        errorKind: "generic",
      }));
      return;
    }
    const controller = new AbortController();
    const asOf = new Date().toISOString();
    setQueryAsOf(asOf);
    setState((previous) => ({
      ...previous,
      initialLoading: true,
      loadingMore: false,
      loadMoreError: false,
      errorKind: null,
    }));
    listSourceItems(
      apiBase,
      {
        asOf,
        sourceKinds: channel ? kindsForChannel(channel) : null,
        textStatuses: status ? [status] : null,
      },
      controller.signal,
    )
      .then((result) => {
        if (controller.signal.aborted || generation.current !== current) return;
        setState({
          items: result.items,
          coverage: result.coverage,
          reason: result.reason,
          answered: result.status === "ok",
          nextCursor: result.nextCursor,
          initialLoading: false,
          loadingMore: false,
          loadMoreError: false,
          restarted: false,
          errorKind: null,
        });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || generation.current !== current) return;
        setState((previous) => ({
          ...previous,
          initialLoading: false,
          errorKind: classifyError(error),
        }));
      });
    return () => controller.abort();
  }, [apiBase, channel, status, reloads]);

  const loadMore = useCallback(() => {
    const previous = latest.current;
    if (
      !apiBase ||
      !queryAsOf ||
      !previous.nextCursor ||
      previous.loadingMore
    ) {
      return;
    }
    const current = generation.current;
    const shown = previous.items.length;
    const cursor = previous.nextCursor;
    const filters = {
      sourceKinds: channel ? kindsForChannel(channel) : null,
      textStatuses: status ? [status] : null,
    };
    const live = () => mounted.current && generation.current === current;
    setState((state) => ({
      ...state,
      loadingMore: true,
      loadMoreError: false,
      restarted: false,
    }));
    void listSourceItems(apiBase, { ...filters, asOf: queryAsOf, cursor })
      .then((result) => {
        if (!live()) return;
        setState((state) => ({
          ...state,
          items: [...state.items, ...result.items],
          nextCursor: result.nextCursor,
          loadingMore: false,
        }));
      })
      .catch(async (error: unknown) => {
        if (!live()) return;
        // The snapshot moved under the cursor. Restarting is what the boundary
        // asks for and what the reader wanted — one more page — so it is done
        // here rather than handed back as a retry that cannot work. The rows
        // already on screen are replaced, not appended to: they were read
        // against a snapshot this answer is no longer bound to.
        if (!isSnapshotRestart(error)) throw error;
        const asOf = new Date().toISOString();
        const read = await restartForward(
          apiBase,
          asOf,
          filters,
          shown + SOURCE_ITEMS_PAGE_SIZE,
        );
        if (!live()) return;
        setQueryAsOf(asOf);
        setState((state) => ({
          ...state,
          items: read.items,
          coverage: read.coverage,
          reason: read.reason,
          answered: read.answered,
          nextCursor: read.nextCursor,
          loadingMore: false,
          restarted: true,
        }));
      })
      .catch(() => {
        if (!live()) return;
        setState((state) => ({
          ...state,
          loadingMore: false,
          loadMoreError: true,
        }));
      });
  }, [apiBase, channel, queryAsOf, status]);

  const reload = useCallback(() => setReloads((value) => value + 1), []);

  return { ...state, queryAsOf, loadMore, reload };
}

export interface SourcesViewProps {
  account?: AuthenticatedAccount | null;
  apiBase: string | null;
  messages?: AppMessages;
  sourceMessages?: SourcesMessages;
  activeView?: AppView;
  onNavigate?: (view: AppView) => void;
  onSignOut?: () => Promise<void> | void;
  /** Injected only in tests; the default performs a full reauthentication reload. */
  reloadPage?: () => void;
}

function defaultReloadPage(): void {
  if (typeof window !== "undefined") window.location.reload();
}

/**
 * The collected-data library: every item Obsina has taken into inventory,
 * listed as one scannable row each, with the canonical text opened in a dialog.
 *
 * It is the product's own evidence that "company memory" is a fact rather than a
 * claim, so it shows what was NOT read as plainly as what was. An unreadable
 * attachment, a mail read only in part and a mail read in full are all rows; the
 * difference is stated in words in the `Text` column, never carried by colour
 * alone.
 */
export function SourcesView({
  account = null,
  apiBase,
  messages = enMessages,
  sourceMessages = sourcesMessages,
  activeView = "sources",
  onNavigate,
  onSignOut,
  reloadPage = defaultReloadPage,
}: SourcesViewProps) {
  const [channel, setChannel] = useState<SourceChannel | null>(null);
  const [status, setStatus] = useState<TextStatus | null>(null);
  const [openItem, setOpenItem] = useState<SourceItem | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  const state = useInventory(apiBase, channel, status);
  // The member's own durable preference, from the one store that already keeps
  // the sidebar's: it is read back from the server on every mount, so the
  // choice made on one machine is the choice the next one starts from.
  const { preferences, setAttachmentGrouping } = useSidebarPreferences();
  const grouping = preferences.attachmentGrouping;
  const trigger = useRef<HTMLElement | null>(null);
  const channelFieldId = useId();
  const statusFieldId = useId();
  const groupingFieldId = useId();

  const signOut = () => {
    if (!onSignOut || signingOut) return;
    setSigningOut(true);
    void Promise.resolve(onSignOut()).finally(() => setSigningOut(false));
  };

  const channelLabel = useCallback(
    (item: SourceItem) => sourceMessages.filters.channel[item.channel],
    [sourceMessages],
  );

  const channelOptions = useMemo(
    () => [
      { value: "", label: sourceMessages.filters.channel.all },
      ...CHANNEL_ORDER.map((value) => ({
        value,
        label: sourceMessages.filters.channel[value],
      })),
    ],
    [sourceMessages],
  );
  const statusOptions = useMemo(
    () => [
      { value: "", label: sourceMessages.filters.text.all },
      ...TEXT_STATUSES.map((value) => ({
        value,
        label: sourceMessages.filters.text[value],
      })),
    ],
    [sourceMessages],
  );
  const groupingOptions = useMemo(
    () => [
      { value: "grouped", label: sourceMessages.filters.grouping.grouped },
      { value: "flat", label: sourceMessages.filters.grouping.flat },
    ],
    [sourceMessages],
  );

  const openRow = (item: SourceItem, element: HTMLElement) => {
    trigger.current = element;
    setOpenItem(item);
  };

  return (
    <AppShell
      account={account}
      activeView={activeView}
      messages={messages}
      onNavigate={onNavigate}
      onSignOut={onSignOut && signOut}
      overlays={
        <SourceTextDialog
          apiBase={apiBase}
          asOf={state.queryAsOf}
          channelLabel={channelLabel}
          item={openItem}
          messages={sourceMessages}
          onOpenChange={(next) => {
            if (next) return;
            setOpenItem(null);
            // Native <dialog> restores focus on close; this makes the guarantee
            // explicit so the row a reader came from is where they land back.
            const element = trigger.current;
            if (element?.isConnected) element.focus();
          }}
          open={openItem !== null}
        />
      }
      signingOut={signingOut}
    >
      <div className="page-stack">
        <div className="page-intro page-intro--with-search">
          <div className="page-intro__topline sources-topline">
            {/*
              The heading itself takes this row, with the two filters in the
              band beside it. The eyebrow that used to sit here was a second
              heading block over "Collected data" and cost the row it stood in;
              the filters need a row regardless, so the title moved into it and
              the page starts one row higher.
            */}
            <h1>{sourceMessages.title}</h1>
            {/* Each filter is sized to its own longest option, not to the page. */}
            <div className="sources-filters">
              <Select
                fieldClassName="sources-filter"
                id={channelFieldId}
                label={sourceMessages.filters.channel.label}
                onChange={(event) =>
                  setChannel(
                    event.target.value === ""
                      ? null
                      : (event.target.value as SourceChannel),
                  )
                }
                options={channelOptions}
                value={channel ?? ""}
              />
              <Select
                fieldClassName="sources-filter"
                id={statusFieldId}
                label={sourceMessages.filters.text.label}
                onChange={(event) =>
                  setStatus(
                    event.target.value === ""
                      ? null
                      : (event.target.value as TextStatus),
                  )
                }
                options={statusOptions}
                value={status ?? ""}
              />
              {/* Set once, remembered. It sits with the filters because it is
                  the same kind of decision — how this list is read — and each
                  control is still sized to its own longest option. */}
              <Select
                fieldClassName="sources-filter"
                id={groupingFieldId}
                label={sourceMessages.filters.grouping.label}
                onChange={(event) =>
                  setAttachmentGrouping(
                    event.target.value as AttachmentGrouping,
                  )
                }
                options={groupingOptions}
                value={grouping}
              />
            </div>
          </div>
          <p>{sourceMessages.body}</p>
        </div>
        <InventoryBody
          grouping={grouping}
          messages={sourceMessages}
          onOpen={openRow}
          reloadPage={reloadPage}
          state={state}
        />
      </div>
    </AppShell>
  );
}

function InventoryBody({
  state,
  grouping,
  messages,
  onOpen,
  reloadPage,
}: {
  state: Inventory;
  grouping: AttachmentGrouping;
  messages: SourcesMessages;
  onOpen: (item: SourceItem, element: HTMLElement) => void;
  reloadPage: () => void;
}) {
  if (state.errorKind !== null) {
    const copy =
      state.errorKind === "unauthorized"
        ? {
            title: messages.errors.unauthorizedTitle,
            body: messages.errors.unauthorizedBody,
            action: messages.errors.unauthorizedAction,
            onAction: reloadPage,
          }
        : state.errorKind === "unavailable"
          ? {
              title: messages.errors.unavailableTitle,
              body: messages.errors.unavailableBody,
              action: messages.errors.retry,
              onAction: state.reload,
            }
          : {
              title: messages.errors.genericTitle,
              body: messages.errors.genericBody,
              action: messages.errors.retry,
              onAction: state.reload,
            };
    return (
      <Callout title={copy.title} tone="danger">
        {copy.body}{" "}
        <Button onClick={copy.onAction} size="sm" variant="secondary">
          {copy.action}
        </Button>
      </Callout>
    );
  }

  if (state.initialLoading) {
    return (
      <div aria-live="polite" className="sources-loading">
        <Skeleton height="1.5rem" />
        <Skeleton height="1.5rem" />
        <Skeleton height="1.5rem" />
      </div>
    );
  }

  // Coverage that is not established is not an empty inventory. The surface
  // refuses to draw a list that would read as complete and says why instead.
  if (!state.answered && state.reason === "coverage_unknown") {
    return (
      <Callout title={messages.coverage.unknownTitle} tone="warning">
        {messages.coverage.unknownBody}
      </Callout>
    );
  }

  if (state.items.length === 0) {
    return (
      <EmptyState
        description={messages.empty.body}
        icon={Library}
        title={messages.empty.title}
      />
    );
  }

  const coverage = state.coverage;
  return (
    <div className="sources-body">
      {/*
        Everything true of the whole list is said once, here, rather than on
        every row: how much is shown, how far the sources were read, and — only
        when it is the case — that some sources are still catching up.
      */}
      <p className="sources-summary">
        <span>{messages.summary.shown(state.items.length)}</span>
        {coverage && (
          <span>{messages.summary.asOf(formatAsOf(coverage.asOf))}</span>
        )}
        {coverage?.state === "partial" && (
          <span className="sources-summary__gap">
            {messages.coverage.partial(
              coverage.reconciledSourceCount,
              coverage.requiredSourceCount,
            )}
          </span>
        )}
      </p>
      <Table minWidth="40rem" regionLabel={messages.table.regionLabel}>
        <TableHead>
          <TableRow>
            <TableHeader>{messages.table.item}</TableHeader>
            <TableHeader>{messages.table.channel}</TableHeader>
            <TableHeader>{messages.table.text}</TableHeader>
            <TableHeader className="sources-cell--numeric">
              {messages.table.size}
            </TableHeader>
            <TableHeader>{messages.table.date}</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {(grouping === "grouped"
            ? groupByParent(state.items)
            : state.items.map((item): InventoryGroup => ({
                item,
                children: [],
              }))
          ).map((group) => (
            <InventoryGroupRows
              group={group}
              key={group.item.sourceItemId}
              messages={messages}
              onOpen={onOpen}
            />
          ))}
        </TableBody>
      </Table>
      {state.nextCursor ? (
        <Button
          disabled={state.loadingMore}
          onClick={state.loadMore}
          size="sm"
          variant="secondary"
        >
          {messages.summary.more}
        </Button>
      ) : (
        coverage?.state === "complete" && (
          // Claimed only when the envelope says every required source is
          // reconciled. Saying "this is everything" over a partial answer would
          // be the one sentence on this page that is not true.
          <p className="sources-footnote">{messages.summary.complete}</p>
        )
      )}
      {state.restarted && (
        // Not an error, and not silence either: the reader asked for one more
        // page and got it, but the rows above it were re-read. The `asOf` line
        // beside the count already moved with them, and this says why.
        <p className="sources-footnote" role="status">
          {messages.summary.moreRestarted}
        </p>
      )}
      {state.loadMoreError && (
        <p className="sources-footnote sources-footnote--error" role="status">
          {messages.summary.moreFailed}
        </p>
      )}
    </div>
  );
}

/**
 * One item and, folded under it, the attachments of it that this list holds.
 *
 * A `<TableBody>` cannot wrap a group in an element of its own without breaking
 * the table, so the fold is expressed on the rows themselves: the control that
 * opens it carries `aria-expanded` and names the item it belongs to, and the
 * child rows are simply not rendered while it is closed. A group with no loaded
 * children draws exactly the row it always drew — no control, no indent, no
 * empty affordance.
 */
function InventoryGroupRows({
  group,
  messages,
  onOpen,
}: {
  group: InventoryGroup;
  messages: SourcesMessages;
  onOpen: (item: SourceItem, element: HTMLElement) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const children = group.children;
  return (
    <>
      <InventoryRow
        expanded={children.length > 0 ? expanded : null}
        item={group.item}
        messages={messages}
        onOpen={onOpen}
        onToggle={() => setExpanded((value) => !value)}
        childCount={children.length}
      />
      {expanded &&
        children.map((child) => (
          <InventoryRow
            item={child}
            key={child.sourceItemId}
            messages={messages}
            nested
            onOpen={onOpen}
          />
        ))}
    </>
  );
}

function InventoryRow({
  item,
  messages,
  onOpen,
  expanded = null,
  childCount = 0,
  onToggle,
  nested = false,
}: {
  item: SourceItem;
  messages: SourcesMessages;
  onOpen: (item: SourceItem, element: HTMLElement) => void;
  /** Fold state, or null when this row holds nothing to fold. */
  expanded?: boolean | null;
  childCount?: number;
  onToggle?: () => void;
  /** True for a row drawn inside another row's fold. */
  nested?: boolean;
}) {
  const label = item.title ?? untitledLabel(item, messages.table);
  // The same function the dialog reads, so the word here and the explanation
  // there are two views of one fact rather than two independent guesses.
  const state = shownTextState(item);
  // Bytes when the provider stated a size (an attachment, a drive file) and
  // characters when the text is what was measured. An item with neither shows
  // nothing rather than a confident zero.
  const size =
    formatByteSize(item.byteSize) ??
    (item.characterCount === null
      ? null
      : messages.table.characters(item.characterCount));
  return (
    <TableRow className={nested ? "sources-row--nested" : undefined}>
      <TableCell>
        <div className="sources-item">
          <button
            className="sources-open"
            onClick={(event) => onOpen(item, event.currentTarget)}
            type="button"
          >
            <span
              className={
                item.title === null ? "sources-open__untitled" : undefined
              }
            >
              {label}
            </span>
          </button>
          {/* The fold rides the row it belongs to rather than a column of its
              own: it is a statement about this item, and a column would have
              printed an empty cell on every row that has no attachment. */}
          {expanded !== null && (
            <button
              aria-expanded={expanded}
              aria-label={
                expanded
                  ? messages.table.attachmentsHide(label)
                  : messages.table.attachmentsShow(label, childCount)
              }
              className="sources-fold"
              onClick={onToggle}
              type="button"
            >
              <Icon
                aria-hidden
                icon={expanded ? ChevronDown : ChevronRight}
                size={13}
              />
              <span aria-hidden>{messages.table.attachments(childCount)}</span>
            </button>
          )}
        </div>
      </TableCell>
      <TableCell>
        <span className="sources-channel">
          <Icon aria-hidden icon={CHANNEL_ICON[item.channel]} size={14} />
          {messages.filters.channel[item.channel]}
        </span>
      </TableCell>
      <TableCell>
        {/*
          The status word is always present, so the tone class only reinforces
          what the text already says.
        */}
        <span className={`sources-status sources-status--${state}`}>
          {messages.state[state]}
        </span>
      </TableCell>
      <TableCell className="sources-cell--numeric">{size ?? ""}</TableCell>
      <TableCell>{formatItemDate(item.occurredAt)}</TableCell>
    </TableRow>
  );
}
